import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Briefcase,
  CheckCircle2,
  Clock,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
} from "lucide-react";
import api from "./api";
import { APP_RELEASE } from "./config/appRelease";

const LAST_EMAIL_STORAGE_KEY = "last_login_email";

/**
 * Login — Redesigned 21/07/2026 (v1.5.0) per Design System
 * "Gruppo Impresa Suite".
 *
 * Layout:
 *   - Sfondo verde pastello #E6F6EE (nessuna foto).
 *   - Card bianca centrata con:
 *       · 3 pallini brand rosso/grigio/oro
 *       · Wordmark "GRUPPO IMPRESA SUITE" (Montserrat Bold) + icona barre
 *       · Nome modulo "Gestionale di Studio" in #08684F (Montserrat SemiBold)
 *       · Campi Email/Password (Inter Medium)
 *       · Recupero password autonomo via email
 *       · Bottone ACCEDI a piena larghezza (#08684F)
 *       · Fascia decorativa a onda in basso
 *   - Footer fuori card: info app + copyright.
 *
 * NB: la LOGICA è invariata dal vecchio Login (state, autofill anti-pattern,
 * hard reset password al mount, sanity storage cleanup). Cambia solo la UI.
 */
export default function Login({ onLoginSuccess, lockReason }) {
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(LAST_EMAIL_STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetComplete, setResetComplete] = useState(false);
  const resetToken = (() => {
    try { return new URLSearchParams(window.location.search).get("reset_token") || ""; }
    catch { return ""; }
  })();

  // Hard reset password al mount (privacy/anti-autofill ritardato Edge/Chrome).
  useEffect(() => {
    setPassword("");
    const el = document.getElementById("op-secret-token");
    if (el) { try { el.value = ""; } catch { /* noop */ } }
    try {
      sessionStorage.removeItem("password");
      sessionStorage.removeItem("op_password");
      localStorage.removeItem("password");
      localStorage.removeItem("op_password");
    } catch { /* noop */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await api.login(email, password);
      setPassword("");
      onLoginSuccess(response.user, email);
    } catch (err) {
      setError(err.message || "Credenziali non valide");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setError("");
    setForgotMessage("");
    setForgotIdentifier(email);
    setForgotMode(true);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotMessage("");
    setLoading(true);
    try {
      const response = await api.requestPasswordReset(forgotIdentifier);
      setForgotMessage(response.message);
    } catch (err) {
      setError(err.message || "Invio email non disponibile");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("La nuova password deve contenere almeno 8 caratteri");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Le due password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.confirmPasswordReset(resetToken, newPassword);
      setNewPassword("");
      setConfirmPassword("");
      setResetComplete(true);
    } catch (err) {
      setError(err.message || "Impossibile reimpostare la password");
    } finally {
      setLoading(false);
    }
  };

  const returnToLogin = () => {
    window.history.replaceState({}, "", window.location.pathname);
    window.location.reload();
  };

  // ── Design tokens locali sono ora centralizzati in index.css come
  //    CSS variables (--gis-green, --gis-gold, --gis-red, ecc.).
  //    Riferimenti puntuali qui via var(--gis-*) evitano duplicati.
  const BRAND_GREEN = "var(--gis-green)";
  const BRAND_GREEN_HOVER = "var(--gis-green-hover)";
  const BG_MINT = "var(--gis-green-50)";
  const DOT_RED = "var(--gis-red)";
  const DOT_GRAY = "var(--gis-gray)";
  const DOT_GOLD = "var(--gis-gold)";

  return (
    <div
      className="min-h-screen w-full flex flex-col p-4 sm:p-6"
      style={{
        backgroundColor: BG_MINT,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
      data-testid="login-page"
    >
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .gis-card { animation: fadeInUp 0.5s ease-out forwards; }
        .gis-heading { font-family: 'Montserrat', 'Inter', sans-serif; letter-spacing: 0.06em; }
        .gis-module  { font-family: 'Montserrat', 'Inter', sans-serif; letter-spacing: -0.005em; }
        .gis-btn     { font-family: 'Montserrat', 'Inter', sans-serif; letter-spacing: 0.08em; }
        .gis-body    { font-family: 'Inter', system-ui, sans-serif; }

        /* Rimuove il bordo giallo default e usa il verde brand */
        .gis-input:focus {
          outline: none;
          border-color: ${BRAND_GREEN} !important;
          box-shadow: 0 0 0 3px rgba(8, 104, 79, 0.12) !important;
        }
      `}</style>

      <div className="flex-1 flex items-center justify-center w-full">
        <div
          className="gis-card w-full max-w-md bg-white rounded-2xl relative overflow-hidden"
          style={{ boxShadow: "0 10px 40px -12px rgba(8, 104, 79, 0.18), 0 4px 12px -4px rgba(0,0,0,0.05)" }}
          data-testid="login-card"
        >
          {/* ── Contenuto card ────────────────────────────────── */}
          <div className="px-8 pt-8 pb-14 sm:px-10 sm:pt-10 sm:pb-16">
            {/* 3 pallini brand */}
            <div className="flex items-center gap-2.5 mb-6" aria-hidden="true">
              <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: DOT_RED }} />
              <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: DOT_GRAY }} />
              <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: DOT_GOLD }} />
            </div>

            {/* Wordmark GRUPPO IMPRESA SUITE */}
            <div className="flex items-center gap-2.5 mb-1">
              <BarChart3 className="h-6 w-6" style={{ color: DOT_GOLD }} strokeWidth={2.5} />
              <span
                className="gis-heading text-[18px] sm:text-[20px] font-bold text-slate-900"
              >
                GRUPPO IMPRESA SUITE
              </span>
            </div>
            <div className="text-[11px] tracking-widest text-slate-400 uppercase mb-8 ml-9 gis-body">
              Gruppo Impresa STP a.r.l.
            </div>

            {/* Nome modulo */}
            <div className="flex items-center gap-3 mb-7">
              <div
                className="flex items-center justify-center w-10 h-10 rounded-lg"
                style={{ backgroundColor: `${BRAND_GREEN}12` }}
              >
                <Briefcase className="h-5 w-5" style={{ color: BRAND_GREEN }} strokeWidth={2.2} />
              </div>
              <h1
                className="gis-module text-2xl sm:text-[26px] font-semibold"
                style={{ color: BRAND_GREEN }}
              >
                Gestionale di Studio
              </h1>
            </div>

            {/* Banner inattività */}
            {lockReason === "inactivity" && !error && (
              <div
                className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-amber-800 mb-4"
                data-testid="login-inactivity-banner"
                role="alert"
              >
                <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm font-medium gis-body">
                  Sessione terminata per inattività. Accedi nuovamente.
                </span>
              </div>
            )}

            {/* Errore */}
            {error && (
              <div
                className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 mb-4"
                data-testid="login-error"
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium gis-body">{error}</span>
              </div>
            )}

            {resetToken ? (
              resetComplete ? (
                <div className="space-y-5 text-center" data-testid="reset-password-success">
                  <CheckCircle2 className="h-12 w-12 mx-auto" style={{ color: BRAND_GREEN }} />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 gis-body">
                      Password aggiornata
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 gis-body">
                      Ora puoi accedere con la nuova password.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={returnToLogin}
                    className="gis-btn w-full h-12 rounded-lg text-white font-semibold uppercase"
                    style={{ backgroundColor: BRAND_GREEN }}
                    data-testid="reset-password-return-login"
                  >
                    Torna all'accesso
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4" autoComplete="off">
                  <div className="flex items-center gap-3 mb-2">
                    <KeyRound className="h-6 w-6" style={{ color: BRAND_GREEN }} />
                    <div>
                      <h2 className="font-semibold text-slate-900 gis-body">Nuova password</h2>
                      <p className="text-xs text-slate-500 gis-body">Minimo 8 caratteri</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-slate-700 font-medium text-sm gis-body">
                      Nuova password
                    </Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      disabled={loading}
                      className="gis-input h-11 px-4 rounded-lg border-slate-200"
                      data-testid="reset-password-new"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-slate-700 font-medium text-sm gis-body">
                      Conferma nuova password
                    </Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      disabled={loading}
                      className="gis-input h-11 px-4 rounded-lg border-slate-200"
                      data-testid="reset-password-confirm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="gis-btn w-full h-12 rounded-lg text-white font-semibold uppercase disabled:opacity-60"
                    style={{ backgroundColor: BRAND_GREEN }}
                    data-testid="reset-password-submit"
                  >
                    {loading ? "Aggiornamento..." : "Reimposta password"}
                  </button>
                </form>
              )
            ) : forgotMode ? (
              <form onSubmit={handleForgotPassword} className="space-y-4" autoComplete="off">
                <div className="flex items-start gap-3">
                  <Mail className="h-6 w-6 mt-0.5" style={{ color: BRAND_GREEN }} />
                  <div>
                    <h2 className="font-semibold text-slate-900 gis-body">Recupera password</h2>
                    <p className="text-sm text-slate-600 gis-body">
                      Riceverai un collegamento personale valido per 30 minuti.
                    </p>
                  </div>
                </div>
                {forgotMessage ? (
                  <div
                    className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800 gis-body"
                    role="status"
                    data-testid="forgot-password-success"
                  >
                    {forgotMessage}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-identifier" className="text-slate-700 font-medium text-sm gis-body">
                      Email o Username
                    </Label>
                    <Input
                      id="forgot-identifier"
                      type="text"
                      value={forgotIdentifier}
                      onChange={(e) => setForgotIdentifier(e.target.value)}
                      required
                      disabled={loading}
                      autoFocus
                      className="gis-input h-11 px-4 rounded-lg border-slate-200"
                      data-testid="forgot-password-identifier"
                    />
                  </div>
                )}
                {!forgotMessage && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="gis-btn w-full h-12 rounded-lg text-white font-semibold uppercase disabled:opacity-60"
                    style={{ backgroundColor: BRAND_GREEN }}
                    data-testid="forgot-password-submit"
                  >
                    {loading ? "Invio in corso..." : "Invia email"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setForgotMode(false);
                    setForgotMessage("");
                    setError("");
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium gis-body"
                  style={{ color: BRAND_GREEN }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Torna all'accesso
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div className="space-y-1.5">
                  <Label htmlFor="login-id" className="text-slate-700 font-medium text-sm gis-body">
                    Email o Username
                  </Label>
                  <Input
                    id="login-id"
                    name="login-id"
                    type="text"
                    placeholder="Inserisci la tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="gis-input h-11 px-4 rounded-lg border-slate-200 text-slate-800 placeholder:text-slate-400 transition-shadow"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-slate-700 font-medium text-sm gis-body">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Inserisci la password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                      className="gis-input h-11 px-4 pr-11 rounded-lg border-slate-200 text-slate-800 placeholder:text-slate-400 transition-shadow"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                      data-testid="login-password-toggle"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-end pt-1">
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="text-sm font-medium hover:underline gis-body"
                    style={{ color: BRAND_GREEN }}
                    data-testid="login-forgot-password"
                  >
                    Password dimenticata?
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="gis-btn w-full h-12 mt-2 rounded-lg text-white font-semibold uppercase tracking-wider transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: BRAND_GREEN }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = BRAND_GREEN_HOVER; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = BRAND_GREEN; }}
                  data-testid="login-submit-button"
                >
                  {loading ? "Accesso in corso..." : "Accedi"}
                </button>
              </form>
            )}
          </div>

          {/* ── Fascia decorativa a onda in basso ─────────────── */}
          <div className="absolute left-0 right-0 bottom-0 pointer-events-none" aria-hidden="true">
            <svg
              viewBox="0 0 448 48"
              preserveAspectRatio="none"
              className="block w-full h-8"
              style={{ display: "block" }}
            >
              <path
                d="M0,32 C80,8 160,48 224,24 C288,0 368,40 448,20 L448,48 L0,48 Z"
                fill={BRAND_GREEN}
                opacity="0.14"
              />
              <path
                d="M0,40 C80,20 160,52 224,32 C288,12 368,48 448,28 L448,48 L0,48 Z"
                fill={BRAND_GREEN}
                opacity="0.28"
              />
              <path
                d="M0,44 C96,28 200,52 320,36 C384,28 416,40 448,36 L448,48 L0,48 Z"
                fill={BRAND_GREEN}
              />
            </svg>
          </div>
        </div>
      </div>

      {/* ── Footer fuori card ─────────────────────────────── */}
      <footer
        className="w-full pt-4 pb-1 flex flex-col md:flex-row md:justify-between md:items-end gap-2 md:gap-4 text-[11px] sm:text-xs leading-tight gis-body text-slate-600"
        data-testid="login-footer"
      >
        <div className="text-center md:text-left" data-testid="login-footer-app-info">
          <div>
            Gestionale di Studio · Portale operativo · v{APP_RELEASE.version}
          </div>
          <div>Ultimo aggiornamento {APP_RELEASE.updatedAt}</div>
        </div>
        <div className="text-center md:text-right" data-testid="login-footer-copyright">
          <div>
            © <span aria-label="marchio registrato">®</span> 2026 · Gruppo Impresa STP a.r.l. · C.F./P.IVA 15948751001
          </div>
          <div>Tutti i diritti riservati.</div>
        </div>
      </footer>
    </div>
  );
}
